import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch, apiUrl, ApiRequestError } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import type { OrgAllowedIdp } from '@grc/types';
import { Button, Card, Field, Input, LoadingPane, Spinner, useToast } from '../../components/ui.js';
import { ZestMark } from '../../components/ZestMark.js';
import {
  allowedProviders,
  idpLabel,
  invitationTokenFrom,
  SSO_PROVIDERS,
  useInvitationPreview,
} from './useInvitationPreview.js';
import './auth.css';

type Provider = OrgAllowedIdp;

// Messages for the `?error=` codes the SSO callback bounces back with when
// sign-in fails on the API side (see apps/worker/src/routes/auth.ts). Unknown
// codes fall back to the generic message.
const SIGN_IN_ERRORS: Record<string, string> = {
  access_denied: 'Sign-in was cancelled. You can try again whenever you are ready.',
  not_allowed:
    'Your organisation is not approved yet. Join the waiting list and we will email you when you can sign in.',
  account_disabled: 'This account has been disabled. Contact your administrator for help.',
  expired: 'Your sign-in session expired. Please try signing in again.',
  sso_failed: 'We could not complete sign-in. Please try again.',
};

export function LoginPage() {
  const { me, loading, refresh } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const requestedFrom = (location.state as { from?: string } | null)?.from;
  // Only allow same-origin app paths to be carried through the SSO flow.
  // Reject protocol-relative and backslash-based URLs, which can otherwise be
  // interpreted as external URLs by the browser or an intermediary.
  const from =
    typeof requestedFrom === 'string' &&
    requestedFrom.startsWith('/') &&
    !requestedFrom.startsWith('//') &&
    !requestedFrom.includes('\\')
      ? requestedFrom
      : '/';
      
  // An invited user is bounced here from /invitations/accept?token=… — the
  // token tells us which organisation they are joining and therefore which
  // sign-in providers to offer.
  const inviteToken = invitationTokenFrom(from);
  const preview = useInvitationPreview(inviteToken);

  const [checkingProvider, setCheckingProvider] = useState<Provider | null>(null);
  const [devEmail, setDevEmail] = useState('');
  const [devName, setDevName] = useState('');
  const [devSubmitting, setDevSubmitting] = useState(false);

  // The SSO callback redirects here with `?error=<code>` when sign-in fails on
  // the API domain (cancelled consent, unapproved company, …). Surface it once
  // as a toast, then strip the param so a refresh doesn't repeat it.
  useEffect(() => {
    const code = params.get('error');
    if (!code) return;
    toast(SIGN_IN_ERRORS[code] ?? SIGN_IN_ERRORS.sso_failed, 'error');
    setParams(
      (prev) => {
        prev.delete('error');
        return prev;
      },
      { replace: true },
    );
  }, [params, setParams, toast]);

  if (loading) return <LoadingPane label="Checking your session" />;
  if (me) return <Navigate to={from} replace />;

  // Where the SSO callback should land the user afterwards. The round trip
  // through the IdP is a full-page navigation, so router state does not
  // survive it — the API carries the path in its signed OAuth cookie instead.
  // Without this an invited user loses `/invitations/accept?token=…` and is
  // dropped on "Create your organisation".
  const ssoUrl = (provider: Provider) =>
    apiUrl(`/auth/login/${provider}`) +
    (from !== '/' ? `?redirect=${encodeURIComponent(from)}` : '');

  const startSso = async (provider: Provider) => {
    setCheckingProvider(provider);
    try {
      // Probe without following the IdP redirect so a 503 (SSO not
      // configured) can be surfaced as a toast instead of a raw error page.
      const res = await fetch(ssoUrl(provider), {
        redirect: 'manual',
        credentials: 'include',
      });
      if (res.status === 503) {
        toast('Single sign-on is not configured on this environment.', 'error');
        return;
      }
      window.location.href = ssoUrl(provider);
    } catch {
      // Cross-origin probe blocked — fall through to a full navigation.
      window.location.href = ssoUrl(provider);
    } finally {
      setCheckingProvider(null);
    }
  };

  const devLogin = async (e: FormEvent) => {
    e.preventDefault();
    setDevSubmitting(true);
    try {
      await apiFetch('/auth/dev-login', {
        method: 'POST',
        body: { email: devEmail.trim(), name: devName.trim() },
      });
      await refresh();
      navigate(from, { replace: true });
    } catch (err) {
      const message =
        err instanceof ApiRequestError ? err.message : 'Sign-in failed. Please try again.';
      toast(message, 'error');
    } finally {
      setDevSubmitting(false);
    }
  };

  const invite = preview.data ?? null;
  const providers = SSO_PROVIDERS.filter((p) =>
    allowedProviders(invite?.allowedIdps ?? null).includes(p.id),
  );

  return (
    <div className="auth-viewport">
      <Card className="auth-card">
        <div className="auth-brand">
          <ZestMark className="auth-logo" />
          <h1>Zest Assure</h1>
        </div>
        <p className="auth-tagline">
          {invite
            ? `Sign in to join ${invite.orgName}`
            : 'Compliance assessments, evidence and reviews — all in one place.'}
        </p>
        {inviteToken && preview.isError && (
          <p className="auth-note">
            This invitation link is invalid or has expired — you can still sign in and ask for a
            new link.
          </p>
        )}
        {invite && providers.length === 1 && (
          <p className="auth-note">
            {invite.orgName} requires sign-in with {idpLabel(providers[0]!.id)}.
          </p>
        )}
        {!invite && (
          <p className="auth-note">
            New here? Access is approved per company —{' '}
            <Link to="/waitlist">join the waiting list</Link>.
          </p>
        )}

        <div className="auth-providers">
          {inviteToken && preview.isPending ? (
            <Spinner label="Checking your invitation" />
          ) : (
            providers.map((provider) => (
              <Button
                key={provider.id}
                variant="secondary"
                className="auth-block-btn"
                loading={checkingProvider === provider.id}
                onClick={() => void startSso(provider.id)}
              >
                {provider.label}
              </Button>
            ))
          )}
        </div>

        {import.meta.env.DEV && (
          <>
            <div className="auth-divider" aria-hidden="true">
              or
            </div>
            <form onSubmit={(e) => void devLogin(e)} aria-label="Development sign-in">
              <p className="auth-note">Development sign-in (local environments only).</p>
              <Field label="Name" required>
                {(props) => (
                  <Input
                    {...props}
                    autoComplete="name"
                    required
                    value={devName}
                    onChange={(e) => setDevName(e.target.value)}
                  />
                )}
              </Field>
              <Field label="Email" required>
                {(props) => (
                  <Input
                    {...props}
                    type="email"
                    autoComplete="email"
                    required
                    value={devEmail}
                    onChange={(e) => setDevEmail(e.target.value)}
                  />
                )}
              </Field>
              <Button type="submit" className="auth-block-btn" loading={devSubmitting}>
                Sign in
              </Button>
            </form>
          </>
        )}
      </Card>

      <nav className="auth-links" aria-label="Zest Assure">
        <a href="https://www.zestassure.com/" target="_blank" rel="noreferrer">
          Home
        </a>
        <a href="https://www.zestassure.com/policies/privacy-policy/" target="_blank" rel="noreferrer">
          Privacy
        </a>
        <a href="https://www.zestassure.com/policies/terms-of-use/" target="_blank" rel="noreferrer">
          Terms of Use
        </a>
      </nav>
    </div>
  );
}
